import { getQueryClient, UseQueryResult } from "eddev/hooks"
import { RouterAPI } from "eddev/routing"
import { proxy, ref, subscribe } from "valtio"
import { subscribeKey } from "valtio/utils"
import { createSearchClient, SearchClient } from "../../scripts/rag-service/src/spec/create-client"
import type { AnySFFRagDocumentResult, SearchIntentParams, SFFRagDocumentTypeName } from "../../server/search/rag"
import { VectorKeywordResult, VectorSearchResults } from "./useSearchClient"
import {
  BrowseFilmTileFragment,
  FilmSessionFragment,
  FilmTileFragment,
  ProgramFilmsManifestFragment,
} from "@generated-types"
import * as fuzzysort from "fuzzysort"
import dayjs from "dayjs"
import { formatDateUri } from "@utils/date-format"

export type SearchInputStore = {
  /** The value shown in the search field */
  inputQuery: string
  /** The query that should be searched for. This is updated after a commit, or a puase */
  searchQuery: string
  /** Whether the user is typing (and the ) */
  typing: boolean
  /** Call every time the input value changes */
  update(value: string, key?: string): void
  /** Replace the search term */
  set(value: string): void
  /** Call when the user hits space/enter etc. It commits the pending value */
  commit(): void
  dispose(): void
}

export type SearchRawResultsStore = {
  vector?: VectorSearchResults
  vectorLoading: boolean
  intent?: SearchIntentParams
  intentLoading: boolean
  localFilms: BrowseFilmTileFragment[]
}

export type SearchDocumentResult = AnySFFRagDocumentResult & {
  isLocalResult?: boolean
}

export type SearchIntentResults = {
  type: "events"
  mode: "films" | "sessions"
  heading: string
  link: string
  tiles: {
    filmId: number
    film: FilmTileFragment
    session?: FilmSessionFragment
    score: number
  }[]
}

export type SearchResultsState = {
  local: {
    documents: SearchDocumentResult[]
    keywords?: VectorKeywordResult[]
  }
  intent: {
    params?: SearchIntentParams
    result?: SearchIntentResults
    loading: boolean
  }
  vector: {
    documents?: SearchDocumentResult[]
    keywords?: VectorKeywordResult[]
    loading: boolean
  }
  totalResults: number
}

export type SearchState = {
  input: SearchInputStore
  program: ProgramFilmsManifestFragment | null
  rawResults: SearchRawResultsStore
  loading: boolean
  hasResults: boolean
  documentResults: SearchDocumentResult[]
  keywordResults: VectorKeywordResult[]
  results: SearchResultsState
}

export class SearchSystem {
  router?: RouterAPI

  state: SearchState

  client: SearchClient

  constructor() {
    this.client = createSearchClient(env.dev && false ? "http://pi-ai:9988" : "https://sff-search.daniellever.com")
    const self = this
    this.state = proxy({
      input: this.createTypingStore(),
      program: null,
      rawResults: {
        vector: undefined,
        vectorLoading: false,
        intent: undefined,
        intentLoading: false,
        localFilms: [],
        totalResults: 0,
      },
      get loading() {
        return self.state.input.inputQuery !== self.state.input.searchQuery || self.state.rawResults.vectorLoading
      },
      get hasResults() {
        if (self.state.rawResults.localFilms?.length! > 0) return true
        if (self.state.rawResults.vector?.documents.length! > 0) return true
        return false
      },
      get documentResults() {
        const localIds = self.state.rawResults.localFilms.map((item) => item.databaseId)
        return [
          ...(self.state.rawResults.localFilms?.map((film): SearchDocumentResult => {
            return {
              id: film.databaseId!,
              title: film.title!,
              data: film! as any,
              score: 1,
              type: "film",
              uri: film.uri!,
              isLocalResult: true,
            }
          }) ?? []),
          ...(self.state.rawResults.vector?.documents ?? []).filter((item) => !localIds.includes(item.id)),
        ]
      },
      get keywordResults() {
        return self.state.rawResults.vector?.keywords ?? []
      },
      get results(): SearchResultsState {
        const raw = self.state.rawResults
        const localIds = raw.localFilms.map((item) => item.databaseId)

        return {
          local: {
            documents:
              raw.localFilms?.map((film): SearchDocumentResult => {
                return {
                  id: film.databaseId!,
                  title: film.title!,
                  data: film! as any,
                  score: 1,
                  type: "film",
                  uri: film.uri!,
                }
              }) ?? [],
          },
          vector: {
            loading: raw.vectorLoading,
            documents: (raw.vector?.documents ?? []).filter((item) => !localIds.includes(item.id)),
          },
          intent: {
            loading: raw.intentLoading,
            params: raw.intent,
            result: raw.intent && self.getProgramResults(raw.intent),
          },
          get totalResults() {
            return this.local.documents.length + (this.vector.documents?.length ?? 0)
          },
        }
      },
    })
    this.handleVectorSearch()
    this.handleLocalFilmSearch()
    this.handleIntentSearch()
  }

  getProgramResults(intent: SearchIntentParams) {
    if (intent.searchTypeName === "serve_top_results") {
      return undefined
    } else if (intent.searchTypeName === "films" || intent.searchTypeName === "sessions") {
      const results: SearchIntentResults = {
        type: "events",
        heading: intent.heading,
        mode: intent.searchTypeName === "films" ? "films" : "sessions",
        link: "",
        tiles: [],
      }

      let browseParams = new Set<string>()
      for (const filter of intent.filmFilters) {
        if (filter.mode === "requireAll" || filter.mode === "requireOne") {
          filter.values.forEach((value) => browseParams.add(`${filter.field}[]=${value}`))
        }
      }
      for (const filter of intent.sessionFilters) {
        if (filter.mode === "requireAll" || filter.mode === "requireOne") {
          filter.values.forEach((value) => browseParams.add(`${filter.field}[]=${value}`))
        }
      }

      const matchFilm = (film: BrowseFilmTileFragment) => {
        let score = 0
        for (const filter of intent.filmFilters) {
          let matcher: (film: BrowseFilmTileFragment, value: string) => boolean
          if (filter.field === "type") {
            matcher = (film, value) => film.eventInfo?.eventType === value
          } else if (filter.field === "strand") {
            matcher = (film, value) => film.strands?.find((strand) => strand?.slug === value) !== undefined
          } else if (filter.field === "classification") {
            matcher = (film, value) => film.eventInfo?.classification === value
          } else if (filter.field === "country") {
            matcher = (film, value) => film.filmInfo?.primaryCountry === value
          } else if (filter.field === "genre") {
            matcher = (film, value) => !!film.filmInfo?.genre?.includes(value)
          } else if (filter.field === "language") {
            matcher = (film, value) => film.filmInfo?.primaryLanguage === value
          }
          const allMatches = filter.values.map((value) => matcher(film, value))
          score += allMatches.reduce((acc, match) => acc + (match ? 1 : 0), 0)
          if (filter.mode === "requireAll" && !allMatches.every((m) => m)) {
            return 0
          } else if (filter.mode === "requireOne" && !allMatches.some((m) => m)) {
            return 0
          }
        }
        return score
      }

      type BrowseSession = NonNullable<BrowseFilmTileFragment["sessions"]>[number]
      const matchSession = (session: BrowseSession) => {
        const sessionDate = dayjs(session?.info?.dateTime)
        let score = 0
        for (const filter of intent.sessionFilters) {
          let matcher: (session: BrowseSession, value: string) => boolean = (session, value) => false
          if (filter.field === "date") {
            matcher = (session, value) => formatDateUri(sessionDate) === value
          } else if (filter.field === "specificHours") {
            matcher = (session, value) => {
              const sessionHour = sessionDate.get("hour")
              return sessionHour === Number(value) || sessionHour + 1 === Number(value)
            }
          } else if (filter.field === "timeOfDay") {
            matcher = (session, value) => {
              const sessionHour = sessionDate.get("hour")
              // Only morning/afternoon/night are supported. A little crossover between periods is ok
              if (value === "morning") {
                return sessionHour >= 5 && sessionHour < 12
              } else if (value === "afternoon") {
                return sessionHour >= 12 && sessionHour < 18
              } else if (value === "night") {
                return sessionHour >= 17 && sessionHour < 21
              }
              return false
            }
          } else if (filter.field === "venues") {
            matcher = (session, value) => {
              return session?.info?.venue?.parentSlug === value
            }
          }
          const allMatches = filter.values.map((value) => matcher(session, String(value)))
          score += allMatches.reduce((acc, match) => acc + (match ? 1 : 0), 0)
          if (filter.mode === "requireAll" && !allMatches.every((m) => m)) {
            return 0
          } else if (filter.mode === "requireOne" && !allMatches.some((m) => m)) {
            return 0
          }
        }
        return score
      }

      console.log("Processing intent results for", JSON.stringify({ ...intent, topResults: undefined }, null, 2))
      for (const film of this.state.program?.currentProgram?.films ?? []) {
        if (!film) continue
        let score = 0

        // Filter film first, because there's less of them
        if (intent.filmFilters.length > 0) {
          score = matchFilm(film as BrowseFilmTileFragment)
          if (!score) continue
        }

        if (intent.searchTypeName === "sessions") {
          for (const session of film.sessions ?? []) {
            const sessionScore = matchSession(session as BrowseSession)
            if (sessionScore > 0) {
              results.tiles.push({
                film: film as FilmTileFragment,
                score: sessionScore,
                filmId: film.databaseId!,
                session: session as FilmSessionFragment,
              })
            }
          }
          results.link = `/program/by-date/?${[...browseParams.values()].join("&")}`
        } else if (intent.searchTypeName === "films") {
          const sessionScores = film.sessions?.map((session) => matchSession(session as BrowseSession)) ?? []
          const maxSessionScore = Math.max(...sessionScores, 0)
          score += maxSessionScore
          results.tiles.push({
            film: film as FilmTileFragment,
            score: score,
            filmId: film.databaseId!,
            session: undefined,
          })
          results.link = `/program/a-z/?${[...browseParams.values()].join("&")}`
        }
      }

      if (results.tiles.length > 0) {
        results.tiles.sort((a, b) => b.score - a.score)
        return results
      }
    }
  }

  setRouter(router: RouterAPI) {
    this.router = router
  }

  setProgram(program: ProgramFilmsManifestFragment) {
    this.state.program = ref(program)
  }

  private createTypingStore() {
    let debounce: any = null
    let timerStartedAt = 0

    const setTyping = () => {
      store.typing = true
      clearTimeout(debounce)
      if (timerStartedAt === 0) {
        timerStartedAt = Date.now()
      }
      let keystrokeDuration = 500
      if (Date.now() - timerStartedAt > 1500) {
        keystrokeDuration = 0
        timerStartedAt = 0
      }
      debounce = setTimeout(() => {
        store.typing = false
        store.searchQuery = store.inputQuery
      }, keystrokeDuration)
    }

    const setFinishedTyping = () => {
      store.typing = false
      timerStartedAt = 0
    }
    const store = proxy<SearchInputStore>({
      inputQuery: "",
      searchQuery: "",
      typing: false,
      update(value, key) {
        store.inputQuery = value
        if (value.trim() === store.searchQuery.trim()) {
          setFinishedTyping()
        } else if (value.trim() === "") {
          store.searchQuery = ""
          setFinishedTyping()
        } else {
          setTyping()
        }
        if (key === " ") {
          store.searchQuery = store.inputQuery
        }
      },
      set(value) {
        store.inputQuery = value
        store.searchQuery = value
        setFinishedTyping()
      },
      commit() {
        setFinishedTyping()
      },
      dispose() {
        clearTimeout(debounce)
      },
    })
    return store
  }

  private handleLocalFilmSearch() {
    if (!env.client || env.admin) return
    subscribeKey(
      this.state.input,
      "inputQuery",
      (q) => {
        if (!this.state.program || !q) {
          this.state.rawResults.localFilms = []
          return
        }
        const results = fuzzysort.go(q, this.state.program?.currentProgram?.films as BrowseFilmTileFragment[], {
          key: "title",
          limit: 8,
          threshold: -10,
        })
        this.state.rawResults.localFilms = results.map((result) => result.obj)
      },
      true,
    )
  }

  private handleVectorSearch() {
    if (!env.client || env.admin) return
    let pendingIndex = 0
    let resultIndex = 0
    let loadingValues = new Set<string>()
    subscribeKey(this.state.input, "searchQuery", (q) => {
      if (!q || !this.state.input.inputQuery) {
        this.state.rawResults.vector = undefined
        this.state.rawResults.vectorLoading = false
        loadingValues.clear()
        return
      }
      loadingValues.add(q)
      const thisIndex = ++pendingIndex
      this.state.rawResults.vectorLoading = true
      getQueryClient()
        .fetchQuery<VectorSearchResults>({
          queryKey: ["site-search", q],
          queryFn: () => {
            return this.client.quickSearch({
              q: q.trim(),
            }) as any as VectorSearchResults
          },
          staleTime: 60 * 1000 * 10,
          networkMode: "always",
          structuralSharing: true,
        })
        .then((value) => {
          if (thisIndex > resultIndex && loadingValues.has(q)) {
            resultIndex = thisIndex
            this.state.rawResults.vector = value
          }
          loadingValues.delete(q)
          this.state.rawResults.vectorLoading = loadingValues.size > 0
        })
    })
  }

  private handleIntentSearch() {
    if (!env.client || env.admin) return
    let abortController: AbortController | null = null
    subscribe(this.state.input, (q) => {
      if (!this.state.input.inputQuery.startsWith(this.state.input.searchQuery)) {
        this.state.rawResults.intent = undefined
      }
    })
    subscribeKey(this.state.input, "searchQuery", (q) => {
      abortController?.abort("outdated")
      const controller = new AbortController()
      abortController = controller

      if (!q || !this.state.input.inputQuery) {
        this.state.rawResults.intent = undefined
        this.state.rawResults.intentLoading = false
        return
      }

      this.state.rawResults.intentLoading = true
      getQueryClient()
        .fetchQuery<any>({
          queryKey: ["site-search-intent", q],
          queryFn: () => {
            return this.client.searchType(
              {
                q: q.trim(),
                preset: "searchIntent",
              },
              {
                signal: controller?.signal,
              },
            )
          },
          staleTime: 60 * 1000 * 10,
          networkMode: "always",
          structuralSharing: true,
        })
        .then((value) => {
          if (abortController === controller) abortController = null
          this.state.rawResults.intent = value
          this.state.rawResults.intentLoading = false
        })
        .catch((error) => {
          if (abortController === controller) {
            abortController = null
            this.state.rawResults.intentLoading = false
          }
        })
    })
  }

  dispose() {
    this.state.input.dispose()
  }
}

export const searchStore = new SearchSystem()
